import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  LayoutDashboard, ShoppingBag, ClipboardList, Users,
  BarChart3, Settings, LogOut, Menu, ChefHat, Truck,
  DollarSign, TrendingUp, Clock, Package, AlertCircle, CreditCard,
  Infinity, ArrowUpCircle, Check, Star, Loader2, CalendarDays, Trophy, Gift, UtensilsCrossed, Monitor, ShoppingCart,
  Zap, Eye, ChevronRight, Megaphone, UserCog, Sparkles, ShieldAlert, Gift as GiftIcon, XCircle, MessageCircle, Instagram, TrendingUp as TrendingUpIcon
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import IFoodIcon from "@/components/icons/IFoodIcon";
import SupportChat from "@/components/SupportChat";
import WhatsAppChatWidget from "@/components/WhatsAppChatWidget";
import ThemeToggle from "@/components/ThemeToggle";
import ClearCacheButton from "@/components/ClearCacheButton";
import AdminNotificationBanner from "@/components/AdminNotificationBanner";
import TrialExpiredModal from "@/components/TrialExpiredModal";
import SuspendedAccountScreen from "@/components/SuspendedAccountScreen";
import { useToast } from "@/hooks/use-toast";
import { usePlanLimits } from "@/hooks/usePlanLimits";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import type { Tables } from "@/integrations/supabase/types";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { getFeatureLabels } from "@/lib/platform-features";
import { motion } from "framer-motion";
import { usePlatformConfig } from "@/hooks/usePlatformConfig";


interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  max_orders_per_month: number | null;
  max_products: number | null;
  max_categories: number | null;
  features: string[];
  is_featured: boolean;
}

interface SidebarItem {
  icon: any;
  label: string;
  path: string;
  featureKey: string | null;
  active?: boolean;
}

interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

const sidebarSections: SidebarSection[] = [
  {
    title: "Principal",
    items: [
      { icon: LayoutDashboard, label: "Dashboard", active: true, path: "/dashboard", featureKey: null },
      { icon: ClipboardList, label: "Pedidos", path: "/pedidos", featureKey: null },
      { icon: ShoppingBag, label: "Cardápio", path: "/gerenciar-cardapio", featureKey: "menu_digital" },
    ],
  },
  {
    title: "Operações",
    items: [
      { icon: Monitor, label: "PDV", path: "/pdv", featureKey: "pdv" },
      { icon: ChefHat, label: "Cozinha", path: "/cozinha", featureKey: "kitchen_panel" },
      { icon: UtensilsCrossed, label: "Mesas", path: "/mesas", featureKey: "table_management" },
      { icon: Truck, label: "Delivery", path: "/delivery", featureKey: "delivery_management" },
      { icon: Package, label: "Estoque", path: "/estoque", featureKey: "stock_management" },
    ],
  },
  {
    title: "Clientes & Vendas",
    items: [
      { icon: Users, label: "Clientes", path: "/clientes", featureKey: "customer_management" },
      { icon: MessageCircle, label: "Chat Clientes", path: "/chat-clientes", featureKey: null },
      { icon: Gift, label: "Fidelidade", path: "/fidelidade", featureKey: "loyalty_program" },
      { icon: Megaphone, label: "Marketing", path: "/marketing", featureKey: "marketing_ai" },
      { icon: TrendingUpIcon, label: "Crescimento", path: "/crescimento", featureKey: "growth_mode" },
    ],
  },
  {
    title: "Financeiro & Relatórios",
    items: [
      { icon: DollarSign, label: "Financeiro", path: "/financeiro", featureKey: "financial_management" },
      { icon: BarChart3, label: "Relatórios", path: "/relatorios", featureKey: "advanced_reports" },
    ],
  },
  {
    title: "Integrações",
    items: [
      { icon: IFoodIcon, label: "iFood", path: "/ifood", featureKey: "ifood_integration" },
      { icon: Instagram, label: "Instagram Shop", path: "/instagram-shop", featureKey: null },
      { icon: MessageCircle, label: "WhatsApp Catálogo", path: "/whatsapp-catalogo", featureKey: null },
      { icon: UserCog, label: "Funcionários", path: "/funcionarios", featureKey: "staff_management" },
    ],
  },
  {
    title: "Sistema",
    items: [
      { icon: Settings, label: "Configurações", path: "/configuracoes", featureKey: null },
    ],
  },
];

const STATUS_MAP: Record<string, { label: string; color: string; dotColor: string }> = {
  received: { label: "Recebido", color: "bg-primary/10 text-primary", dotColor: "bg-primary" },
  preparing: { label: "Em preparo", color: "bg-amber-500/10 text-amber-600", dotColor: "bg-amber-500" },
  ready: { label: "Pronto", color: "bg-emerald-500/10 text-emerald-600", dotColor: "bg-emerald-500" },
  out_for_delivery: { label: "Saiu p/ entrega", color: "bg-blue-500/10 text-blue-600", dotColor: "bg-blue-500" },
  completed: { label: "Finalizado", color: "bg-muted text-muted-foreground", dotColor: "bg-muted-foreground" },
  cancelled: { label: "Cancelado", color: "bg-destructive/10 text-destructive", dotColor: "bg-destructive" },
};

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "12px",
  fontSize: "12px",
  color: "hsl(var(--foreground))",
  boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
};

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState<Tables<"restaurants"> | null>(null);
  const [orders, setOrders] = useState<Tables<"orders">[]>([]);
  const [orderItems, setOrderItems] = useState<Tables<"order_items">[]>([]);
  const [products, setProducts] = useState<Tables<"products">[]>([]);
  const [profileName, setProfileName] = useState("");
  const [ratings, setRatings] = useState<any[]>([]);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [blockedAttempts, setBlockedAttempts] = useState<{ count: number; latest: string | null }>({ count: 0, latest: null });
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const planLimits = usePlanLimits(restaurant?.id || null);
  const trialStatus = useTrialStatus(restaurant);
  const { config: platformConfig } = usePlatformConfig();

  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [upgrading, setUpgrading] = useState(false);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState<{ code: string; type: string; value: number } | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [pendingCheckoutPlanId, setPendingCheckoutPlanId] = useState<string | null>(null);
  const [showPaymentGate, setShowPaymentGate] = useState(false);
  const [paymentGatePlanName, setPaymentGatePlanName] = useState("");
  const [paymentGateLoading, setPaymentGateLoading] = useState(false);
  const [subscriptionDaysLeft, setSubscriptionDaysLeft] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { navigate("/login"); return; }

        const { data: profile } = await supabase
          .from("profiles").select("full_name").eq("user_id", user.id).maybeSingle();
        if (profile?.full_name) setProfileName(profile.full_name);

        const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
        let rest: any = null;
        if (impersonateId) {
          const { data } = await supabase.from("restaurants").select("*").eq("id", impersonateId).maybeSingle();
          rest = data;
        } else {
          const { data } = await supabase.from("restaurants").select("*").eq("owner_id", user.id).maybeSingle();
          rest = data;
        }

        if (!rest) { setLoading(false); return; }
        setRestaurant(rest);

        // Check if user is on an active trial period
        const trialEndsAt = (rest as any).trial_ends_at;
        const trialStartedAt = (rest as any).trial_started_at;
        const isOnActiveTrial = trialStartedAt && trialEndsAt && new Date(trialEndsAt).getTime() > Date.now();

        // Check access: paid plan without subscription OR trial already used without plan
        if (rest.plan_id) {
          const { data: planData } = await supabase
            .from("plans")
            .select("name, price_monthly")
            .eq("id", rest.plan_id)
            .maybeSingle();

          if (planData && Number(planData.price_monthly) > 0) {
            // If user is on active trial, don't block — let them use the system
            if (!isOnActiveTrial) {
              // Check for active subscription
              const { data: existingSub } = await supabase
                .from("subscriptions")
                .select("id, status, current_period_end")
                .eq("restaurant_id", rest.id)
                .eq("status", "active")
                .maybeSingle();

              // Check if subscription exists AND is not expired
              const isSubValid = existingSub && existingSub.current_period_end &&
                new Date(existingSub.current_period_end).getTime() > Date.now();

              if (!isSubValid) {
                // Check if there's a pending payment (PIX generated but not yet paid)
                const { data: pendingSub } = await supabase
                  .from("subscriptions")
                  .select("id, status")
                  .eq("restaurant_id", rest.id)
                  .eq("status", "pending")
                  .maybeSingle();

                if (pendingSub) {
                  // User has a pending payment — allow access, don't block
                  console.log("Subscription pending payment, allowing dashboard access");
                } else {
                  // No valid subscription and no active trial — show payment gate
                  setPendingCheckoutPlanId(rest.plan_id);
                  setPaymentGatePlanName(planData.name);
                  setShowPaymentGate(true);
                }
              }
            }
          }
        } else if (rest.trial_used && !isOnActiveTrial) {
          // Trial already used, expired, and no plan — must subscribe
          setShowPaymentGate(true);
        }

        // Check subscription days left for renewal alert
        const { data: activeSub } = await supabase
          .from("subscriptions")
          .select("current_period_end")
          .eq("restaurant_id", rest.id)
          .eq("status", "active")
          .maybeSingle();
        if (activeSub?.current_period_end) {
          const dLeft = Math.max(0, Math.ceil((new Date(activeSub.current_period_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
          setSubscriptionDaysLeft(dLeft);
        }

        const [ordersRes, productsRes, ratingsRes] = await Promise.all([
          supabase.from("orders").select("*").eq("restaurant_id", rest.id).order("created_at", { ascending: false }),
          supabase.from("products").select("*").eq("restaurant_id", rest.id),
          supabase.from("order_ratings" as any).select("*").eq("restaurant_id", rest.id).order("created_at", { ascending: false }).limit(20),
        ]);

        const allOrders = ordersRes.data || [];
        setOrders(allOrders);
        setProducts(productsRes.data || []);
        setRatings((ratingsRes.data as any[]) || []);

        const orderIds = allOrders.map((o) => o.id);
        if (orderIds.length > 0) {
          const { data: items } = await supabase.from("order_items").select("*").in("order_id", orderIds);
          setOrderItems(items || []);
        }
        // Fetch blocked order attempts (this month)
        const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
        const { count: blockedCount } = await supabase
          .from("blocked_order_attempts" as any)
          .select("id", { count: "exact", head: true })
          .eq("restaurant_id", rest.id)
          .gte("created_at", firstDayOfMonth);

        if (blockedCount && blockedCount > 0) {
          const { data: latestBlocked } = await supabase
            .from("blocked_order_attempts" as any)
            .select("created_at")
            .eq("restaurant_id", rest.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          setBlockedAttempts({ count: blockedCount, latest: (latestBlocked as any)?.created_at || null });
        }

        // Fetch latest changelog version
        const { data: latestChangelog } = await supabase
          .from("changelogs")
          .select("version, created_at")
          .eq("is_published", true)
          .eq("visibility", "public")
          .order("release_date", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (latestChangelog?.version) {
          setLatestVersion(latestChangelog.version);
          const seenVersion = localStorage.getItem("viamenu_seen_version");
          setHasNewVersion(seenVersion !== latestChangelog.version);
        }
      } catch (err) {
        console.error("Dashboard load error:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  // Realtime subscription + polling for blocked order attempts
  useEffect(() => {
    if (!restaurant) return;
    const restaurantId = restaurant.id;

    const fetchBlocked = async () => {
      const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
      const { count } = await supabase
        .from("blocked_order_attempts" as any)
        .select("id", { count: "exact", head: true })
        .eq("restaurant_id", restaurantId)
        .gte("created_at", firstDay);

      if (count && count > 0) {
        const { data: latest } = await supabase
          .from("blocked_order_attempts" as any)
          .select("created_at")
          .eq("restaurant_id", restaurantId)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        setBlockedAttempts({ count, latest: (latest as any)?.created_at || null });
      } else {
        setBlockedAttempts({ count: 0, latest: null });
      }
    };

    // Realtime channel
    const channel = supabase
      .channel(`blocked-attempts-${restaurantId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "blocked_order_attempts",
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        () => {
          fetchBlocked();
          toast({
            title: "⚠️ Pedido bloqueado!",
            description: "Um cliente tentou fazer um pedido, mas o limite do seu plano foi atingido.",
            variant: "destructive",
          });
        }
      )
      .subscribe();

    // Polling fallback every 30s
    const interval = setInterval(fetchBlocked, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [restaurant?.id]);

  // Payment gate handlers
  const handlePaymentGateCheckout = async () => {
    if (!pendingCheckoutPlanId || !restaurant) return;
    setPaymentGateLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          plan_id: pendingCheckoutPlanId,
          restaurant_id: restaurant.id,
          billing_cycle: "monthly",
        },
      });
      if (error) throw error;
      if (data.free) {
        toast({ title: "Plano ativado!" });
        window.location.reload();
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("Não foi possível gerar o link de pagamento");
      }
    } catch (err: any) {
      toast({ title: "Erro ao iniciar pagamento", description: err.message, variant: "destructive" });
    } finally {
      setPaymentGateLoading(false);
    }
  };

  const handlePaymentGateFreeTrial = async () => {
    if (!restaurant) return;
    setPaymentGateLoading(true);
    try {
      // Find free plan
      const { data: freePlan } = await supabase
        .from("plans")
        .select("id")
        .eq("is_active", true)
        .eq("price_monthly", 0)
        .maybeSingle();

      const trialEndsAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      await supabase
        .from("restaurants")
        .update({
          plan_id: freePlan?.id || null,
          trial_started_at: new Date().toISOString(),
          trial_ends_at: trialEndsAt,
          trial_used: true,
        })
        .eq("id", restaurant.id);

      toast({ title: "Teste gratuito ativado!", description: "Você tem 30 dias para experimentar a plataforma." });
      window.location.reload();
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setPaymentGateLoading(false);
    }
  };

  const handlePaymentGateSkip = async () => {
    if (!restaurant) return;
    setPaymentGateLoading(true);
    try {
      // Remove plan — menu will be blocked via FeatureGuard
      await supabase
        .from("restaurants")
        .update({ plan_id: null })
        .eq("id", restaurant.id);

      toast({ title: "Plano removido", description: "Seu cardápio digital ficará bloqueado até ativar um plano." });
      window.location.reload();
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setPaymentGateLoading(false);
    }
  };

  useEffect(() => {
    const payment = searchParams.get("payment");
    if (payment === "success") {
      toast({ title: "Pagamento aprovado!", description: "Seu plano foi atualizado com sucesso." });
      setShowPaymentGate(false);
      // Refresh restaurant data to clear trial/blocked alerts
      setBlockedAttempts({ count: 0, latest: null });
      const refreshRestaurant = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: rest } = await supabase
          .from("restaurants")
          .select("*")
          .eq("owner_id", user.id)
          .maybeSingle();
        if (rest) setRestaurant(rest);
      };
      refreshRestaurant();
    } else if (payment === "failure") {
      toast({ title: "Pagamento não aprovado", description: "Tente novamente.", variant: "destructive" });
      if (restaurant) {
        setShowPaymentGate(true);
      }
    } else if (payment === "pending") {
      toast({ title: "Pagamento pendente", description: "Você receberá uma confirmação em breve." });
      if (restaurant) {
        setShowPaymentGate(true);
      }
    }
    if (searchParams.get("upgrade") === "true" && !loading) openUpgradeDialog();
  }, [searchParams, toast, loading, restaurant]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const openUpgradeDialog = async () => {
    const { data } = await supabase.from("plans").select("*").eq("is_active", true).order("sort_order", { ascending: true });
    setPlans((data as any[] || []).map(p => ({ ...p, features: getFeatureLabels(p.features) })));
    setUpgradeOpen(true);
  };

  const handleUpgrade = async (plan: Plan) => {
    if (!restaurant) return;
    setUpgrading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          plan_id: plan.id,
          restaurant_id: restaurant.id,
          billing_cycle: billingCycle,
          coupon_code: couponApplied?.code || undefined,
        },
      });
      if (error) throw error;
      if (data.free) {
        toast({ title: "Plano atualizado!", description: `Agora você está no plano ${plan.name}.${data.discount > 0 ? ` Desconto de R$ ${data.discount.toFixed(2)} aplicado!` : ""}` });
        setUpgradeOpen(false);
        window.location.reload();
        return;
      }
      if (data.checkout_url) window.location.href = data.checkout_url;
      else throw new Error("Não foi possível gerar o link de pagamento");
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setUpgrading(false);
    }
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    try {
      const { data, error } = await supabase
        .from("platform_coupons")
        .select("code, type, value, valid_until, max_uses, uses_count, applicable_plan_ids, billing_cycle, free_days, for_new_users_only")
        .eq("code", couponCode.trim().toUpperCase())
        .eq("is_active", true)
        .maybeSingle();

      if (error || !data) throw new Error("Cupom não encontrado");
      if (data.valid_until && new Date(data.valid_until) < new Date()) throw new Error("Cupom expirado");
      if (data.max_uses && data.uses_count >= data.max_uses) throw new Error("Cupom esgotado");
      if ((data as any).for_new_users_only) throw new Error("Este cupom é válido apenas para novos cadastros");

      setCouponApplied({ code: data.code, type: data.type, value: data.value });
      toast({ title: "Cupom aplicado!", description: `${data.type === "percentage" ? `${data.value}%` : `R$ ${data.value.toFixed(2)}`} de desconto` });
    } catch (err: any) {
      toast({ title: "Cupom inválido", description: err.message, variant: "destructive" });
      setCouponApplied(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Computed stats
  const today = new Date().toISOString().slice(0, 10);
  const todayOrders = orders.filter((o) => o.created_at.slice(0, 10) === today);
  const completedOrders = orders.filter((o) => o.status !== "cancelled");
  const revenueToday = todayOrders.filter(o => o.status !== "cancelled").reduce((sum, o) => sum + Number(o.total), 0);
  const totalRevenue = completedOrders.reduce((sum, o) => sum + Number(o.total), 0);
  const avgTicket = todayOrders.length > 0 ? revenueToday / todayOrders.length : 0;
  const activeProducts = products.filter((p) => p.is_available).length;

  // Yesterday comparison
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const yesterdayRevenue = orders.filter(o => o.created_at.slice(0, 10) === yesterday && o.status !== "cancelled").reduce((s, o) => s + Number(o.total), 0);
  const revenueChange = yesterdayRevenue > 0 ? ((revenueToday - yesterdayRevenue) / yesterdayRevenue * 100) : 0;

  const revenueByDay = useMemo(() => {
    const days: { date: string; label: string; revenue: number; orders: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const dayOrders = orders.filter((o) => o.created_at.slice(0, 10) === key && o.status !== "cancelled");
      days.push({
        date: key,
        label: d.toLocaleDateString("pt-BR", { weekday: "short", day: "numeric" }),
        revenue: dayOrders.reduce((s, o) => s + Number(o.total), 0),
        orders: dayOrders.length,
      });
    }
    return days;
  }, [orders]);

  const topProducts = useMemo(() => {
    const map = new Map<string, { name: string; qty: number; revenue: number }>();
    orderItems.forEach((item) => {
      const existing = map.get(item.product_name) || { name: item.product_name, qty: 0, revenue: 0 };
      existing.qty += item.quantity;
      existing.revenue += Number(item.total_price);
      map.set(item.product_name, existing);
    });
    return Array.from(map.values()).sort((a, b) => b.qty - a.qty).slice(0, 5);
  }, [orderItems]);

  const ordersByType = useMemo(() => {
    const map: Record<string, number> = {};
    completedOrders.forEach((o) => { map[o.order_type] = (map[o.order_type] || 0) + 1; });
    return map;
  }, [completedOrders]);

  const ratingsStats = useMemo(() => {
    if (ratings.length === 0) return null;
    const avgFood = ratings.reduce((s, r) => s + Number(r.food_rating), 0) / ratings.length;
    const avgService = ratings.reduce((s, r) => s + Number(r.service_rating), 0) / ratings.length;
    const deliveryRatings = ratings.filter((r) => r.delivery_rating != null);
    const avgDelivery = deliveryRatings.length > 0 ? deliveryRatings.reduce((s, r) => s + Number(r.delivery_rating), 0) / deliveryRatings.length : null;
    const categories = avgDelivery != null ? [avgFood, avgService, avgDelivery] : [avgFood, avgService];
    const avgOverall = categories.reduce((a, b) => a + b, 0) / categories.length;
    const distribution = [0, 0, 0, 0, 0];
    ratings.forEach((r) => {
      const vals = [Number(r.food_rating), Number(r.service_rating)];
      if (r.delivery_rating != null) vals.push(Number(r.delivery_rating));
      const avg = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
      distribution[avg - 1]++;
    });
    return { avgFood, avgService, avgDelivery, avgOverall, distribution, total: ratings.length, deliveryCount: deliveryRatings.length };
  }, [ratings]);

  const stats = [
    { label: "Faturamento Hoje", value: formatCurrency(revenueToday), icon: DollarSign, change: revenueChange, accent: true },
    { label: "Pedidos Hoje", value: String(todayOrders.length), icon: ClipboardList, change: null, accent: false },
    { label: "Ticket Médio", value: formatCurrency(avgTicket), icon: TrendingUp, change: null, accent: false },
    { label: "Faturamento Total", value: formatCurrency(totalRevenue), icon: CalendarDays, change: null, accent: false },
  ];

  const recentOrders = orders.slice(0, 8);
  const pendingOrders = orders.filter(o => ["received", "preparing"].includes(o.status)).length;

  const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "short", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
          </div>
          <p className="text-muted-foreground text-sm">Carregando painel...</p>
        </div>
      </div>
    );
  }

  if (restaurant && (restaurant as any).status === 'suspended') {
    return <SuspendedAccountScreen restaurantName={restaurant.name} restaurantId={restaurant.id} />;
  }

  if (showPaymentGate && restaurant) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-lg w-full"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold font-display mb-2">
              {restaurant.trial_used && !pendingCheckoutPlanId ? "Assinatura Necessária" : "Pagamento Pendente"}
            </h1>
            <p className="text-muted-foreground">
              {restaurant.trial_used && !pendingCheckoutPlanId
                ? "Seu período de teste gratuito já foi utilizado. Para continuar usando a plataforma, escolha um plano pago."
                : <>Você escolheu o plano <span className="font-semibold text-foreground">{paymentGatePlanName}</span>, mas o pagamento ainda não foi realizado.</>
              }
            </p>
          </div>

          <div className="space-y-3">
            {/* Option 1: Pay now */}
            <button
              onClick={pendingCheckoutPlanId ? handlePaymentGateCheckout : openUpgradeDialog}
              disabled={paymentGateLoading}
              className="w-full p-5 rounded-xl border-2 border-primary bg-primary/5 hover:bg-primary/10 transition-all text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center shrink-0">
                  <CreditCard className="h-5 w-5 text-primary-foreground" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold font-display text-foreground mb-1">
                    {pendingCheckoutPlanId ? `Realizar Pagamento` : "Escolher um Plano Pago"}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {pendingCheckoutPlanId
                      ? `Ativar o plano ${paymentGatePlanName} e ter acesso completo a todos os recursos.`
                      : "Veja os planos disponíveis e assine para ter acesso completo."
                    }
                  </p>
                </div>
                {paymentGateLoading ? (
                  <Loader2 className="h-5 w-5 text-primary animate-spin mt-1" />
                ) : (
                  <ArrowUpCircle className="h-5 w-5 text-primary mt-1 group-hover:translate-x-0.5 transition-transform" />
                )}
              </div>
            </button>

            {/* Option 2: Free trial — only if not already used */}
            {!restaurant.trial_used && (
            <button
              onClick={handlePaymentGateFreeTrial}
              disabled={paymentGateLoading}
              className="w-full p-5 rounded-xl border-2 border-border hover:border-primary/40 transition-all text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center shrink-0">
                  <Gift className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold font-display text-foreground mb-1">Teste Gratuito de 30 dias</h3>
                  <p className="text-sm text-muted-foreground">
                    Use o plano gratuito por 30 dias sem compromisso. Faça upgrade quando quiser.
                  </p>
                </div>
                <Badge variant="secondary" className="shrink-0 mt-1">Grátis</Badge>
              </div>
            </button>
            )}

            {/* Option 3: Skip (blocks menu) — only if trial not used */}
            {!restaurant.trial_used && (
            <button
              onClick={handlePaymentGateSkip}
              disabled={paymentGateLoading}
              className="w-full p-4 rounded-xl border border-border hover:border-destructive/40 transition-all text-left group"
            >
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                  <XCircle className="h-4 w-4 text-destructive" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-sm text-foreground">Continuar sem plano</h3>
                  <p className="text-xs text-muted-foreground">
                    Seu cardápio digital ficará <span className="font-semibold text-destructive">bloqueado</span> até ativar um plano.
                  </p>
                </div>
              </div>
            </button>
            )}
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Você pode alterar seu plano a qualquer momento nas configurações.
          </p>
        </motion.div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <motion.div {...fadeUp} className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-xl font-bold font-display mb-2">Nenhum restaurante encontrado</h2>
          <p className="text-muted-foreground mb-6">Você ainda não cadastrou um restaurante. Cadastre agora para começar a usar o painel.</p>
          <Button onClick={() => navigate("/cadastro")} size="lg">Cadastrar restaurante</Button>
        </motion.div>
      </div>
    );
  }

  const sidebarContent = (
    <>
      <div className="h-16 border-b border-border/50 flex items-center px-4 gap-3">
        {(() => {
          const logoSrc = platformConfig.dashboard_icon_url || platformConfig.logo_url;
          return logoSrc ? (
            <img src={logoSrc} alt={platformConfig.logo_text} className="w-9 h-9 rounded-xl object-contain shrink-0" loading="eager" />
          ) : (
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shrink-0 shadow-lg shadow-primary/20">
              <span className="text-primary-foreground font-bold text-xs">{platformConfig.logo_initials || "VM"}</span>
            </div>
          );
        })()}
        {(sidebarOpen || mobileSidebarOpen) && (
          <div className="min-w-0">
            <span className="font-display font-bold text-sm">{platformConfig.logo_text || "ViaMenu"}</span>
            <p className="text-[9px] text-muted-foreground truncate">{restaurant.name}</p>
          </div>
        )}
      </div>
      <nav className="flex-1 p-2.5 space-y-3 overflow-y-auto">
        {sidebarSections.map((section) => {
          const visibleItems = section.items.filter(item => !item.featureKey || planLimits.hasFeature(item.featureKey));
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.title}>
              {(sidebarOpen || mobileSidebarOpen) && (
                <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                  {section.title}
                </p>
              )}
              {!sidebarOpen && !mobileSidebarOpen && (
                <div className="mx-auto my-1 w-5 border-t border-border/40" />
              )}
              <div className="space-y-0.5">
                {visibleItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => {
                      if (item.path) navigate(item.path);
                      setMobileSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                      item.active
                        ? "bg-primary/10 text-primary font-medium shadow-sm"
                        : "text-muted-foreground hover:bg-secondary/80 hover:text-foreground"
                    }`}
                  >
                    <item.icon className={`h-[18px] w-[18px] shrink-0 ${item.active ? "text-primary" : ""}`} />
                    {(sidebarOpen || mobileSidebarOpen) && <span className="truncate">{item.label}</span>}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border/50 space-y-1">
        <div className="flex items-center justify-center gap-1">
          <ClearCacheButton />
          <ThemeToggle collapsed={!sidebarOpen && !mobileSidebarOpen} />
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" />
          {(sidebarOpen || mobileSidebarOpen) && <span>Sair</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileSidebarOpen(false)}
          />
          <motion.aside
            initial={{ x: -280 }} animate={{ x: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-72 h-full bg-card border-r border-border flex flex-col z-10 shadow-2xl"
          >
            {sidebarContent}
          </motion.aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className={`hidden md:flex ${sidebarOpen ? "w-64" : "w-[68px]"} border-r border-border/50 bg-card/80 backdrop-blur-sm transition-all duration-300 flex-col`}>
        {sidebarContent}
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto min-w-0">
        {/* Header */}
        <header className="h-auto min-h-[56px] md:h-16 border-b border-border/50 flex flex-wrap items-center justify-between px-4 md:px-6 py-2 gap-2 bg-card/50 backdrop-blur-sm sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => window.innerWidth < 768 ? setMobileSidebarOpen(!mobileSidebarOpen) : setSidebarOpen(!sidebarOpen)}
              className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
            >
              <Menu className="h-5 w-5 text-muted-foreground" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-display font-bold text-base md:text-lg">Dashboard</h1>
                {pendingOrders > 0 && (
                  <Badge variant="destructive" className="text-[10px] h-5 px-1.5 animate-pulse">
                    {pendingOrders} pendente{pendingOrders > 1 ? "s" : ""}
                  </Badge>
                )}
              </div>
              <p className="text-[10px] md:text-xs text-muted-foreground truncate">
                {restaurant.name} • {formatDate(new Date().toISOString())}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={() => window.open(`/${restaurant.slug}`, "_blank")}
            >
              <Eye className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Ver Cardápio</span>
            </Button>
            <button
              onClick={async () => {
                if (!restaurant) return;
                const newValue = !(restaurant as any).is_manually_closed;
                const { error } = await supabase.from("restaurants").update({ is_manually_closed: newValue } as any).eq("id", restaurant.id);
                if (!error) {
                  setRestaurant({ ...restaurant, is_manually_closed: newValue } as any);
                  toast({ title: newValue ? "Loja fechada manualmente" : "Loja reaberta!" });
                }
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] md:text-xs font-semibold transition-all ${
                (restaurant as any).is_manually_closed
                  ? "bg-destructive/10 text-destructive hover:bg-destructive/20"
                  : "bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20"
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${(restaurant as any).is_manually_closed ? "bg-destructive" : "bg-emerald-500 animate-pulse"}`} />
              {(restaurant as any).is_manually_closed ? "Fechada" : "Aberta"}
            </button>
            {profileName && (
              <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-border/50">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xs font-bold text-primary">{profileName.charAt(0).toUpperCase()}</span>
                </div>
                <span className="text-sm font-medium text-foreground">{profileName}</span>
              </div>
            )}
          </div>
        </header>

        <TrialExpiredModal
          trialDaysLeft={trialStatus.trialDaysLeft}
          trialExpired={trialStatus.trialExpired}
          isOnTrial={trialStatus.isOnTrial}
          trialEndsAt={trialStatus.trialEndsAt}
          trialStartedAt={(restaurant as any)?.trial_started_at}
          onUpgrade={openUpgradeDialog}
        />

        <div className="p-4 md:p-6 space-y-5">
          {/* Admin Notifications */}
          <AdminNotificationBanner restaurantId={restaurant.id} />

          {/* Blocked Order Attempts Alert */}
          {blockedAttempts.count > 0 && planLimits.maxOrdersPerMonth !== null && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-3 p-4 rounded-xl border bg-destructive/10 border-destructive/20"
            >
              <ShieldAlert className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-destructive">
                  {blockedAttempts.count} pedido{blockedAttempts.count > 1 ? "s" : ""} bloqueado{blockedAttempts.count > 1 ? "s" : ""} este mês
                </p>
                <p className="text-xs text-destructive/80 mt-0.5">
                  Clientes tentaram fazer pedidos, mas o limite do seu plano foi atingido. Faça upgrade para não perder vendas!
                </p>
                {blockedAttempts.latest && (
                  <p className="text-[10px] text-destructive/60 mt-1">
                    Última tentativa: {new Date(blockedAttempts.latest).toLocaleString("pt-BR")}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="destructive"
                onClick={openUpgradeDialog}
                className="shrink-0 gap-1.5"
              >
                <ArrowUpCircle className="h-3.5 w-3.5" />
                Fazer Upgrade
              </Button>
            </motion.div>
          )}
          
          {/* Subscription renewal alert */}
          {subscriptionDaysLeft !== null && subscriptionDaysLeft <= 3 && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex items-center gap-3 p-4 rounded-xl border ${
                subscriptionDaysLeft <= 1
                  ? "bg-destructive/10 border-destructive/20"
                  : "bg-amber-500/10 border-amber-500/20"
              }`}
            >
              <AlertCircle className={`h-5 w-5 shrink-0 ${subscriptionDaysLeft <= 1 ? "text-destructive" : "text-amber-600"}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${subscriptionDaysLeft <= 1 ? "text-destructive" : "text-amber-700 dark:text-amber-400"}`}>
                  {subscriptionDaysLeft === 0
                    ? "Sua assinatura expira hoje!"
                    : `Sua assinatura expira em ${subscriptionDaysLeft} dia${subscriptionDaysLeft !== 1 ? "s" : ""}!`
                  }
                </p>
                <p className={`text-xs ${subscriptionDaysLeft <= 1 ? "text-destructive/80" : "text-amber-600/80 dark:text-amber-400/70"}`}>
                  Renove agora para não perder acesso aos recursos do seu plano.
                </p>
              </div>
              <Button
                size="sm"
                variant={subscriptionDaysLeft <= 1 ? "destructive" : "outline"}
                onClick={() => navigate("/configuracoes/faturamento")}
                className="shrink-0 gap-1.5"
              >
                <CreditCard className="h-3.5 w-3.5" />
                Renovar
              </Button>
            </motion.div>
          )}

          {/* Greeting */}
          <motion.div {...fadeUp} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-lg md:text-xl font-display font-bold">
                {new Date().getHours() < 12 ? "Bom dia" : new Date().getHours() < 18 ? "Boa tarde" : "Boa noite"}{profileName ? `, ${profileName.split(" ")[0]}` : ""}! 👋
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">Aqui está o resumo do seu restaurante.</p>
            </div>
            {latestVersion && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (latestVersion) localStorage.setItem("viamenu_seen_version", latestVersion);
                  setHasNewVersion(false);
                  navigate("/changelog");
                }}
                className={`gap-2 ${hasNewVersion ? "border-primary/50 text-primary" : ""}`}
              >
                <Sparkles className="h-4 w-4" />
                <span>v{latestVersion}{hasNewVersion ? " • Ver Novidades" : ""}</span>
                {hasNewVersion && <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-primary" /></span>}
              </Button>
            )}
          </motion.div>

          {/* KPI Stats */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`group relative p-4 md:p-5 rounded-2xl border border-border/60 bg-card shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden ${
                  s.accent ? "ring-1 ring-primary/10" : ""
                }`}
              >
                {s.accent && (
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
                )}
                <div className="relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                      s.accent ? "bg-primary/10" : "bg-secondary"
                    }`}>
                      <s.icon className={`h-4 w-4 ${s.accent ? "text-primary" : "text-muted-foreground"}`} />
                    </div>
                    {s.change !== null && s.change !== 0 && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        s.change > 0 ? "bg-emerald-500/10 text-emerald-600" : "bg-destructive/10 text-destructive"
                      }`}>
                        {s.change > 0 ? "+" : ""}{s.change.toFixed(0)}%
                      </span>
                    )}
                  </div>
                  <p className="text-xl md:text-2xl font-bold font-display truncate">{s.value}</p>
                  <p className="text-[10px] md:text-xs text-muted-foreground mt-1">{s.label}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Revenue Chart + Top Products */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <motion.div
              {...fadeUp}
              transition={{ delay: 0.15 }}
              className="lg:col-span-2 rounded-2xl border border-border/60 bg-card shadow-sm p-5"
            >
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="font-display font-semibold text-sm flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-primary" />
                    Faturamento — 7 dias
                  </h2>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Total: <strong className="text-foreground">{formatCurrency(revenueByDay.reduce((s, d) => s + d.revenue, 0))}</strong>
                    {" • "}{revenueByDay.reduce((s, d) => s + d.orders, 0)} pedidos
                  </p>
                </div>
              </div>
              {revenueByDay.some((d) => d.revenue > 0) ? (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={revenueByDay}>
                    <defs>
                      <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `R$${v}`} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number) => [formatCurrency(value), "Faturamento"]}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#revenueGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
                  Sem dados de faturamento nos últimos 7 dias
                </div>
              )}
            </motion.div>

            {/* Top Products */}
            <motion.div
              {...fadeUp}
              transition={{ delay: 0.2 }}
              className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden"
            >
              <div className="p-5 pb-3">
                <h2 className="font-display font-semibold text-sm flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-amber-500" />
                  Mais Vendidos
                </h2>
              </div>
              {topProducts.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
                  Sem dados ainda
                </div>
              ) : (
                <div className="divide-y divide-border/40">
                  {topProducts.map((p, i) => {
                    const maxQty = topProducts[0]?.qty || 1;
                    return (
                      <div key={p.name} className="flex items-center gap-3 px-5 py-3 hover:bg-secondary/30 transition-colors">
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
                          i === 0 ? "bg-amber-500/10 text-amber-600" :
                          i === 1 ? "bg-slate-400/10 text-slate-500" :
                          i === 2 ? "bg-orange-700/10 text-orange-700" :
                          "bg-secondary text-muted-foreground"
                        }`}>
                          {i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{p.name}</p>
                          <div className="mt-1.5 h-1.5 rounded-full bg-secondary overflow-hidden">
                            <div className="h-full rounded-full bg-primary/60 transition-all" style={{ width: `${(p.qty / maxQty) * 100}%` }} />
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs font-bold">{p.qty}</p>
                          <p className="text-[9px] text-muted-foreground">{formatCurrency(p.revenue)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </div>

          {/* Orders by Type */}
          {Object.keys(ordersByType).length > 0 && (
            <motion.div {...fadeUp} transition={{ delay: 0.25 }} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {([
                { key: "dine_in", label: "No Local", icon: "🍽️", bg: "from-blue-500/5 to-blue-500/0" },
                { key: "takeout", label: "Retirada", icon: "🥡", bg: "from-amber-500/5 to-amber-500/0" },
                { key: "delivery", label: "Delivery", icon: "🛵", bg: "from-emerald-500/5 to-emerald-500/0" },
                { key: "scheduled", label: "Agendado", icon: "📅", bg: "from-purple-500/5 to-purple-500/0" },
              ] as const).filter(t => ordersByType[t.key]).map((t) => (
                <div key={t.key} className={`p-4 rounded-2xl border border-border/60 bg-gradient-to-br ${t.bg} bg-card text-center shadow-sm`}>
                  <span className="text-2xl">{t.icon}</span>
                  <p className="text-2xl font-bold font-display mt-1">{ordersByType[t.key]}</p>
                  <p className="text-xs text-muted-foreground">{t.label}</p>
                </div>
              ))}
            </motion.div>
          )}

          {/* Recent Orders */}
          <motion.div {...fadeUp} transition={{ delay: 0.3 }} className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
            <div className="p-5 border-b border-border/50 flex items-center justify-between">
              <div>
                <h2 className="font-display font-semibold text-sm">Pedidos Recentes</h2>
                <p className="text-[10px] text-muted-foreground mt-0.5">{orders.length} pedidos no total</p>
              </div>
              <Button variant="ghost" size="sm" className="text-xs h-8 gap-1" onClick={() => navigate("/pedidos")}>
                Ver todos <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
            {recentOrders.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4">
                  <ClipboardList className="h-7 w-7 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground font-medium text-sm">Nenhum pedido ainda</p>
                <p className="text-xs text-muted-foreground mt-1">Os pedidos aparecerão aqui em tempo real</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-[11px] text-muted-foreground border-b border-border/50 bg-secondary/30">
                      <th className="text-left px-5 py-3 font-medium">Pedido</th>
                      <th className="text-left px-5 py-3 font-medium">Cliente</th>
                      <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Tipo</th>
                      <th className="text-left px-5 py-3 font-medium">Total</th>
                      <th className="text-left px-5 py-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((o) => {
                      const st = STATUS_MAP[o.status] || { label: o.status, color: "bg-muted", dotColor: "bg-muted-foreground" };
                      const typeLabels: Record<string, string> = {
                        dine_in: "Mesa", takeout: "Retirada", delivery: "Delivery", scheduled: "Agendado",
                      };
                      return (
                        <tr key={o.id} className="border-b border-border/30 hover:bg-secondary/20 transition-colors">
                          <td className="px-5 py-3.5">
                            <span className="text-sm font-mono font-medium">#{o.id.slice(0, 6)}</span>
                          </td>
                          <td className="px-5 py-3.5 text-sm">{o.customer_name || "—"}</td>
                          <td className="px-5 py-3.5 text-sm text-muted-foreground hidden md:table-cell">
                            {typeLabels[o.order_type] || o.order_type}
                          </td>
                          <td className="px-5 py-3.5 text-sm font-semibold">{formatCurrency(Number(o.total))}</td>
                          <td className="px-5 py-3.5">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${st.color}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${st.dotColor}`} />
                              {st.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>

          {/* Plan & Limits */}
          <motion.div {...fadeUp} transition={{ delay: 0.35 }} className="rounded-2xl border border-border/60 bg-card shadow-sm p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <CreditCard className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h2 className="font-display font-semibold text-sm">Seu Plano</h2>
                  <p className="text-[10px] text-muted-foreground">{planLimits.planName}</p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={openUpgradeDialog} className="text-xs h-8 gap-1.5 rounded-full">
                <ArrowUpCircle className="h-3.5 w-3.5" />
                Upgrade
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              {[
                { label: "Produtos", current: planLimits.currentProducts, max: planLimits.maxProducts },
                { label: "Categorias", current: planLimits.currentCategories, max: planLimits.maxCategories },
                { label: "Pedidos/mês", current: planLimits.currentMonthOrders, max: planLimits.maxOrdersPerMonth },
              ].map(({ label, current, max }) => (
                <div key={label}>
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-semibold">{current}/{max ?? "∞"}</span>
                  </div>
                  <Progress
                    value={max ? (current / max) * 100 : 10}
                    className="h-2.5 rounded-full"
                  />
                </div>
              ))}
            </div>
            {(!planLimits.canAddProduct || !planLimits.canAcceptOrder) && (
              <div className="mt-4 p-3 rounded-xl bg-destructive/5 border border-destructive/10 text-destructive text-xs flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>Você atingiu o limite do seu plano.</span>
                <button onClick={openUpgradeDialog} className="underline font-medium ml-auto shrink-0">Fazer upgrade</button>
              </div>
            )}
          </motion.div>

          {/* Menu Summary */}
          <motion.div {...fadeUp} transition={{ delay: 0.4 }} className="rounded-2xl border border-border/60 bg-card shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-semibold text-sm flex items-center gap-2">
                <ShoppingBag className="h-4 w-4 text-primary" />
                Resumo do Cardápio
              </h2>
              <Button variant="ghost" size="sm" className="text-xs h-8 gap-1" onClick={() => navigate("/gerenciar-cardapio")}>
                Gerenciar <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 rounded-xl bg-secondary/30">
                <p className="text-2xl font-bold font-display">{products.length}</p>
                <p className="text-[10px] text-muted-foreground mt-1">Total</p>
              </div>
              <div className="p-4 rounded-xl bg-emerald-500/5">
                <p className="text-2xl font-bold font-display text-emerald-600">{activeProducts}</p>
                <p className="text-[10px] text-muted-foreground mt-1">Disponíveis</p>
              </div>
              <div className="p-4 rounded-xl bg-destructive/5">
                <p className="text-2xl font-bold font-display text-destructive">{products.length - activeProducts}</p>
                <p className="text-[10px] text-muted-foreground mt-1">Esgotados</p>
              </div>
            </div>
          </motion.div>

          {/* Customer Ratings */}
          <motion.div {...fadeUp} transition={{ delay: 0.45 }} className="rounded-2xl border border-border/60 bg-card shadow-sm p-5">
            <h2 className="font-display font-semibold text-sm flex items-center gap-2 mb-5">
              <Star className="h-4 w-4 text-amber-500" />
              Avaliações dos Clientes
            </h2>
            {ratingsStats ? (
              <>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <div className="flex flex-col items-center justify-center text-center">
                    <p className="text-5xl font-bold font-display">{ratingsStats.avgOverall.toFixed(1)}</p>
                    <div className="flex gap-0.5 mt-2 mb-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`h-4 w-4 ${s <= Math.round(ratingsStats.avgOverall) ? "fill-amber-400 text-amber-400" : "fill-none text-muted-foreground/20"}`} />
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground">{ratingsStats.total} avaliação{ratingsStats.total !== 1 ? "ões" : ""}</p>
                  </div>

                  <div className="space-y-4">
                    {[
                      { label: "🍽️ Comida", value: ratingsStats.avgFood },
                      { label: "👋 Atendimento", value: ratingsStats.avgService },
                      ...(ratingsStats.avgDelivery != null ? [{ label: "🛵 Entrega", value: ratingsStats.avgDelivery }] : []),
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs text-muted-foreground">{label}</span>
                          <span className="text-xs font-bold">{value.toFixed(1)}</span>
                        </div>
                        <div className="h-2 rounded-full bg-secondary overflow-hidden">
                          <div className="h-full rounded-full bg-amber-400 transition-all" style={{ width: `${(value / 5) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map((star) => {
                      const count = ratingsStats.distribution[star - 1];
                      const pct = ratingsStats.total > 0 ? (count / ratingsStats.total) * 100 : 0;
                      return (
                        <div key={star} className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-3 text-right">{star}</span>
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400 shrink-0" />
                          <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                            <div className="h-full rounded-full bg-amber-400/60 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[10px] text-muted-foreground w-5 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Tags summary */}
                {(() => {
                  const allTags: Record<string, number> = {};
                  ratings.forEach(r => {
                    const tags = Array.isArray(r.tags) ? r.tags : [];
                    tags.forEach((t: string) => { allTags[t] = (allTags[t] || 0) + 1; });
                  });
                  const sorted = Object.entries(allTags).sort((a, b) => b[1] - a[1]);
                  if (sorted.length === 0) return null;
                  return (
                    <div className="mt-5 pt-4 border-t border-border/50">
                      <h3 className="text-xs font-semibold mb-3">Tags mais frequentes</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {sorted.slice(0, 10).map(([tag, count]) => (
                          <span key={tag} className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-[11px] font-medium">
                            {tag} <span className="text-primary/60">({count})</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {ratings.filter(r => r.comment).length > 0 && (
                  <div className="mt-5 pt-4 border-t border-border/50">
                    <h3 className="text-xs font-semibold mb-3">Comentários recentes</h3>
                    <div className="space-y-2.5 max-h-60 overflow-y-auto">
                      {ratings.filter(r => r.comment).slice(0, 8).map((r) => (
                        <div key={r.id} className="p-3 rounded-xl bg-secondary/40">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <Star key={s} className={`h-3 w-3 ${s <= Math.round((Number(r.food_rating) + Number(r.service_rating)) / 2) ? "fill-amber-400 text-amber-400" : "fill-none text-muted-foreground/20"}`} />
                              ))}
                            </div>
                            <span className="text-[10px] text-muted-foreground">
                              {new Date(r.created_at).toLocaleDateString("pt-BR", { day: "numeric", month: "short" })}
                            </span>
                          </div>
                          {Array.isArray(r.tags) && r.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-1.5">
                              {r.tags.map((t: string) => (
                                <span key={t} className="px-2 py-0.5 rounded-full bg-accent text-accent-foreground text-[9px] font-medium">{t}</span>
                              ))}
                            </div>
                          )}
                          <p className="text-sm text-foreground/80">{r.comment}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4">
                  <Star className="h-7 w-7 text-muted-foreground/30" />
                </div>
                <p className="text-sm font-medium text-muted-foreground">Nenhuma avaliação recebida ainda.</p>
                <p className="text-xs text-muted-foreground mt-1">As avaliações aparecerão quando clientes avaliarem seus pedidos.</p>
              </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeOpen} onOpenChange={(open) => { setUpgradeOpen(open); if (!open) { setCouponCode(""); setCouponApplied(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Escolher Plano</DialogTitle>
          </DialogHeader>

          <div className="flex items-center justify-center gap-1 mb-3 p-1 rounded-xl bg-secondary/50 w-fit mx-auto">
            {(["monthly", "yearly"] as const).map((c) => (
              <button
                key={c}
                onClick={() => setBillingCycle(c)}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                  billingCycle === c ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {c === "monthly" ? "Mensal" : "Anual"}
                {c === "yearly" && <Badge variant="secondary" className="ml-1.5 text-[9px]">Economia</Badge>}
              </button>
            ))}
          </div>

          {/* Coupon Input */}
          <div className="flex items-center gap-2 mb-4 px-1">
            <div className="flex-1 relative">
              <Input
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponApplied(null); }}
                placeholder="Código do cupom (opcional)"
                className="h-9 text-sm font-mono pr-20"
              />
              {couponApplied && (
                <Badge className="absolute right-2 top-1/2 -translate-y-1/2 bg-emerald-500 text-white text-[10px]">
                  <Check className="h-3 w-3 mr-1" />
                  {couponApplied.type === "percentage" ? `${couponApplied.value}%` : `R$ ${couponApplied.value}`}
                </Badge>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={validateCoupon}
              disabled={validatingCoupon || !couponCode.trim() || !!couponApplied}
              className="h-9 text-xs"
            >
              {validatingCoupon ? <Loader2 className="h-3 w-3 animate-spin" /> : "Aplicar"}
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {plans.map((plan) => {
              const basePrice = billingCycle === "yearly" && plan.price_yearly ? plan.price_yearly : plan.price_monthly;
              const planAny = plan as any;
              const hasPromo = (planAny.promo_price_monthly || planAny.promo_price_yearly) &&
                (!planAny.promo_ends_at || new Date(planAny.promo_ends_at) > new Date());
              const promoPrice = hasPromo
                ? (billingCycle === "yearly" && planAny.promo_price_yearly ? planAny.promo_price_yearly : planAny.promo_price_monthly || basePrice)
                : basePrice;
              
              let displayPrice = promoPrice;
              if (couponApplied) {
                if (couponApplied.type === "percentage") {
                  displayPrice = promoPrice * (1 - couponApplied.value / 100);
                } else {
                  displayPrice = Math.max(0, promoPrice - couponApplied.value);
                }
              }

              const isCurrentPlan = planLimits.planName === plan.name;

              return (
                <div
                  key={plan.id}
                  className={`relative p-5 rounded-2xl border-2 transition-all ${
                    plan.is_featured ? "border-primary shadow-lg shadow-primary/5" : "border-border/60"
                  } ${isCurrentPlan ? "bg-primary/5" : ""}`}
                >
                  {plan.is_featured && (
                    <Badge className="absolute -top-2.5 right-3 bg-primary text-primary-foreground text-[10px] shadow-sm">
                      <Star className="h-3 w-3 mr-0.5" /> Popular
                    </Badge>
                  )}
                  {hasPromo && planAny.promo_label && (
                    <Badge className="absolute -top-2.5 left-3 bg-emerald-500 text-white text-[10px] shadow-sm">
                      {planAny.promo_label}
                    </Badge>
                  )}
                  <h3 className="font-display font-bold">{plan.name}</h3>
                  <p className="text-xs text-muted-foreground mb-3">{plan.description}</p>
                  <div className="mb-4">
                    {(hasPromo || couponApplied) && displayPrice !== basePrice ? (
                      <>
                        <span className="text-sm line-through text-muted-foreground">{formatCurrency(basePrice)}</span>
                        <span className="text-3xl font-bold font-display ml-1 text-emerald-500">{formatCurrency(displayPrice)}</span>
                      </>
                    ) : (
                      <span className="text-3xl font-bold font-display">{formatCurrency(displayPrice)}</span>
                    )}
                    <span className="text-xs text-muted-foreground">/{billingCycle === "yearly" ? "ano" : "mês"}</span>
                  </div>
                  <div className="space-y-1.5 mb-4">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Pedidos/mês</span>
                      <span className="font-medium">{plan.max_orders_per_month ?? "∞"}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Produtos</span>
                      <span className="font-medium">{plan.max_products ?? "∞"}</span>
                    </div>
                  </div>
                  {plan.features.length > 0 && (
                    <ul className="space-y-1.5 mb-4 pt-3 border-t border-border/50">
                      {plan.features.slice(0, 4).map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-[11px] text-muted-foreground">
                          <Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button
                    size="sm"
                    className="w-full rounded-xl"
                    variant={isCurrentPlan ? "outline" : "default"}
                    disabled={isCurrentPlan || upgrading}
                    onClick={() => handleUpgrade(plan)}
                  >
                    {upgrading ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrentPlan ? "Plano atual" : plan.price_monthly === 0 ? "Selecionar" : "Assinar"}
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Impersonation banner */}
      {sessionStorage.getItem("admin_impersonate_restaurant_id") && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-destructive text-destructive-foreground text-center py-1.5 text-xs font-medium">
          ⚠️ Modo Admin — Visualizando como lojista
          <button onClick={() => { sessionStorage.removeItem("admin_impersonate_restaurant_id"); navigate("/admin"); }} className="ml-3 underline hover:no-underline">
            Voltar ao painel admin
          </button>
        </div>
      )}

      {restaurant && !sessionStorage.getItem("admin_impersonate_restaurant_id") && (
        <SupportChat restaurantId={restaurant.id} />
      )}

      {restaurant && (
        <WhatsAppChatWidget restaurantId={restaurant.id} />
      )}
    </div>
  );
};

export default Dashboard;
